import { ApiGradesResponse, ClimbType } from "griptonite-api-client";

import queryClient from "../core/queryClient";

const getGradesData = () => {
  return queryClient.getQueryData<ApiGradesResponse>(["grades"]);
};

const getGradeSystemValueMap = (system: string) => {
  const gradesData = getGradesData();
  if (gradesData === undefined) {
    throw new Error("Grades data not loaded");
  }
  const mapping = gradesData.mappings[system];
  if (mapping === undefined) {
    throw new Error(`Grade system ${system} not found`);
  }
  return mapping;
};

export function getPinGradeValue(value: string | null, system: string) {
  if (value === null) {
    return null;
  }
  if (["FONT", "FONTFULL", "FRENCH", "FRENCH_ABV", "FRENCH_B"].includes(system)) {
    return value.slice(1);
  }
  if (system === "YDS") {
    return value.slice(2);
  }
  const customSystems = getGradesData()?.customSystems;
  if (customSystems?.[system]) {
    return customSystems[system].pinValues[value];
  }
  return value;
}

export function convertToInternal(value: string | null, system: string) {
  if (value === null) {
    return null;
  }
  const mapping = getGradeSystemValueMap(system);
  const internalValue = mapping[value];
  if (internalValue === undefined) {
    throw new Error(`Invalid value ${value} for grade system ${system}`);
  }
  return internalValue;
}

export function convertFromInternal(
  value: number | null,
  system: string,
  method: "floor" | "round" = "floor",
): string | null {
  if (value === null) {
    return null;
  }
  const mapping = getGradeSystemValueMap(system);
  const entries = Object.entries(mapping);
  if (method === "floor") {
    let prevKey = entries[0][0];
    for (const [key, gradeInternal] of entries) {
      if (value < gradeInternal) {
        return prevKey;
      }
      prevKey = key;
    }
    return prevKey;
  } else if (method === "round") {
    let prev = entries[0];
    for (const [key, gradeInternal] of entries) {
      if (value < gradeInternal) {
        if (gradeInternal - value < value - prev[1]) {
          return key;
        } else {
          return prev[0];
        }
      }
      prev = [key, gradeInternal];
    }
    return prev[0];
  } else {
    throw new Error(`Invalid method ${method}`);
  }
}

export function convertBetweenSystems(
  value: string | null,
  fromSystem: string,
  toSystem: string,
  method: "floor" | "round" = "round",
) {
  if (fromSystem === toSystem) {
    return value;
  }
  if (value === null) {
    return null;
  }
  const internalValue = convertToInternal(value, fromSystem);
  return convertFromInternal(internalValue, toSystem, method);
}

export function getAllGradeValuesForSystem(system: string): string[] {
  const mapping = getGradeSystemValueMap(system);
  return Object.keys(mapping);
}

export function getGradeValuesBetween(
  system: string,
  min: string | null,
  max: string | null,
): string[] {
  const mapping = getGradeSystemValueMap(system);
  const entries = Object.entries(mapping);
  const minInternal = min === null ? 0 : mapping[min];
  const maxInternal = max === null ? entries[entries.length - 1][1] : mapping[max];
  return entries
    .filter(([_, value]) => value >= minInternal && value <= maxInternal)
    .map(([key, _]) => key);
}

export function getGradeSystemsForClimbType(climbType: ClimbType) {
  return {
    BOULDER: ["FONT", "FONTFULL", "FRENCH_B", "HUECO"],
    SPORT: ["FRENCH", "FRENCH_ABV", "UIAA", "YDS", "NORWEGIAN", "DANKYU", "EWBANK"],
  }[climbType];
}
